import React, { useEffect, useMemo, useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    TextField,
    Button,
    Chip,
    InputAdornment,
    IconButton,
    Tooltip,
} from '@mui/material';
import { Search as SearchIcon, CameraAlt as CameraIcon } from '@mui/icons-material';
import { Add as AddIcon } from '@mui/icons-material';
import { Product } from '../../../types';
import { formatCurrencyBDT } from '../../../utils/currency';
import { BarcodeScannerDialog } from '../../../components/common/BarcodeScannerDialog';
import { ProductThumbnail } from '../../../components/common/ProductThumbnail';

const LISTBOX_ID = "pos-product-results";
const optionId = (productId: string) => `pos-product-${productId}`;

interface ProductSearchProps {
    products: Product[];
    searchTerm: string;
    onSearchChange: (term: string) => void;
    onProductSelect: (product: Product) => void;
    onQuickAddProduct: () => void;
    searchInputRef?: React.RefObject<HTMLInputElement | null>;
}

export const ProductSearch: React.FC<ProductSearchProps> = ({
    products,
    searchTerm,
    onSearchChange,
    onProductSelect,
    onQuickAddProduct,
    searchInputRef,
}) => {
    const [scannerOpen, setScannerOpen] = useState(false);

    const handleBarcodeScan = (barcode: string) => {
        onSearchChange(barcode);
    };

    // Products are already filtered by the Redux hook
    const filteredProducts = products;

    // What Enter picks while searching: an exact barcode/SKU match (so a
    // barcode scanner, which types the code then presses Enter, always gets
    // its product), otherwise the top item of the list.
    const enterTarget = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term || filteredProducts.length === 0) return null;
        return (
            filteredProducts.find(
                (p) => p.barcode?.toLowerCase() === term || p.sku?.toLowerCase() === term,
            ) ?? filteredProducts[0]
        );
    }, [searchTerm, filteredProducts]);

    // ↑/↓ move the highlight from there, and Enter adds the highlighted one.
    // Typing starts over, so a barcode scan still lands on its exact match.
    // (Reset while rendering, so a new search never shows a stale highlight.)
    const [arrowedId, setArrowedId] = useState<string | null>(null);
    const [arrowedFor, setArrowedFor] = useState(searchTerm);
    if (searchTerm !== arrowedFor) {
        setArrowedFor(searchTerm);
        setArrowedId(null);
    }
    const highlighted =
        (arrowedId && filteredProducts.find((p) => p.product_id === arrowedId)) || enterTarget;

    const moveHighlight = (step: 1 | -1) => {
        if (filteredProducts.length === 0) return;
        const current = highlighted ? filteredProducts.indexOf(highlighted) : -1;
        const next = Math.min(Math.max(current + step, 0), filteredProducts.length - 1);
        setArrowedId(filteredProducts[next].product_id);
    };

    // Keep the highlighted row on screen while arrowing through a long list.
    useEffect(() => {
        if (arrowedId) document.getElementById(optionId(arrowedId))?.scrollIntoView({ block: "nearest" });
    }, [arrowedId]);

    return (
        <>
            <Card
                sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <CardContent sx={{ flex: 1, p: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ display: "flex", gap: 2, mb: { xs: 1.5, sm: 3 } }}>
                        <TextField
                            fullWidth
                            autoFocus
                            placeholder="Search by name, SKU, or barcode..."
                            value={searchTerm}
                            onChange={(e) => onSearchChange(e.target.value)}
                            helperText="↑ ↓ to choose · Enter to add"
                            onKeyDown={(e) => {
                                const plain = !e.altKey && !e.shiftKey && !e.ctrlKey && !e.metaKey;
                                if (e.key === "Escape") {
                                    onSearchChange("");
                                    setArrowedId(null);
                                } else if ((e.key === "ArrowDown" || e.key === "ArrowUp") && plain) {
                                    e.preventDefault(); // move the highlight, not the caret
                                    moveHighlight(e.key === "ArrowDown" ? 1 : -1);
                                } else if (
                                    e.key === "Enter" &&
                                    highlighted &&
                                    !e.nativeEvent.isComposing &&
                                    // Ctrl/⌘+Enter is the checkout shortcut, not "add this product".
                                    !e.ctrlKey &&
                                    !e.metaKey
                                ) {
                                    e.preventDefault();
                                    // Let go of focus first, as a click does, so
                                    // the quantity dialog's input reliably gets it.
                                    (e.target as HTMLElement).blur();
                                    onProductSelect(highlighted);
                                }
                            }}
                            inputRef={searchInputRef}
                            inputProps={{
                                role: "combobox",
                                "aria-autocomplete": "list",
                                "aria-controls": LISTBOX_ID,
                                "aria-expanded": filteredProducts.length > 0,
                                "aria-activedescendant": highlighted ? optionId(highlighted.product_id) : undefined,
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "text.secondary" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title="Scan barcode with camera">
                                            <IconButton
                                                size="small"
                                                onClick={() => setScannerOpen(true)}
                                                edge="end"
                                            >
                                                <CameraIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    <List
                        dense
                        id={LISTBOX_ID}
                        role="listbox"
                        aria-label="Products"
                        sx={{
                            maxHeight: "calc(100vh - 300px)",
                            overflow: "auto",
                            width: "100%",
                            bgcolor: "background.paper",
                            "& .MuiListItem-root": {
                                px: { xs: 0.5, sm: 2 },
                                py: 1.5,
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                "&:hover": {
                                    bgcolor: "action.hover",
                                    cursor: "pointer",
                                },
                                "&:last-child": {
                                    borderBottom: "none",
                                },
                            },
                        }}
                    >
                        {filteredProducts.map((product) => {
                            const availableStock = product.stock
                                ? Math.max(
                                      0,
                                      product.stock.quantity -
                                          (product.stock.reserved_quantity || 0)
                                  )
                                : 0;
                            const isLowStock =
                                availableStock > 0 && availableStock <= 5;
                            const isOutOfStock = availableStock <= 0;

                            return (
                                <ListItem
                                    key={product.product_id}
                                    id={optionId(product.product_id)}
                                    role="option"
                                    aria-selected={product === highlighted}
                                    onClick={() => onProductSelect(product)}
                                    sx={{
                                        // The item Enter would add, so the cashier sees it first.
                                        ...(product === highlighted && {
                                            bgcolor: "action.selected",
                                            boxShadow: (t) => `inset 3px 0 0 ${t.palette.primary.main}`,
                                        }),
                                        "&:hover": {
                                            "& .MuiListItemSecondaryAction-root": {
                                                visibility: "visible",
                                            },
                                        },
                                    }}
                                >
                                    <ListItemAvatar>
                                        <ProductThumbnail
                                            images={product.images}
                                            alt={product.name}
                                        />
                                    </ListItemAvatar>
                                    <ListItemText
                                        // Both lines hold chips/boxes (divs),
                                        // so they render as divs, not <h6>/<p>.
                                        slotProps={{ secondary: { component: "div" } }}
                                        primary={
                                            <Typography
                                                variant="subtitle1"
                                                component="div"
                                                sx={{
                                                    fontWeight: 500,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    flexWrap: "wrap",
                                                    gap: 1,
                                                    lineHeight: 1.35,
                                                }}
                                            >
                                                {product.name}
                                                {product === highlighted && (
                                                    <Chip
                                                        label="Enter ↵"
                                                        size="small"
                                                        sx={{ height: 20, fontSize: 11 }}
                                                    />
                                                )}
                                                <Chip
                                                    label={
                                                        isOutOfStock
                                                            ? "Out of Stock"
                                                            : `${availableStock} in stock`
                                                    }
                                                    size="small"
                                                    color={
                                                        isOutOfStock
                                                            ? "error"
                                                            : isLowStock
                                                            ? "warning"
                                                            : "success"
                                                    }
                                                    variant="outlined"
                                                    sx={{
                                                        ml: { xs: 0, sm: 1 },
                                                        fontWeight: 500,
                                                    }}
                                                />
                                            </Typography>
                                        }
                                        secondary={
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    flexWrap: "wrap",
                                                    columnGap: 1,
                                                    rowGap: 0.5,
                                                    mt: 0.5,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        flexWrap: "wrap",
                                                        gap: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            fontFamily: "monospace",
                                                            fontSize: "0.75rem",
                                                            bgcolor: "action.hover",
                                                            px: 0.75,
                                                            py: 0.25,
                                                            borderRadius: 1,
                                                        }}
                                                    >
                                                        {product.sku}
                                                    </Typography>
                                                    {product.brand?.name && (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                fontStyle: "italic",
                                                            }}
                                                        >
                                                            {product.brand.name}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {product.is_on_sale &&
                                                product.effective_price !=
                                                    null ? (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "baseline",
                                                            gap: 0.75,
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                textDecoration:
                                                                    "line-through",
                                                                color: "text.disabled",
                                                            }}
                                                        >
                                                            {formatCurrencyBDT(
                                                                Number(
                                                                    product.selling_price,
                                                                ) || 0,
                                                            )}
                                                        </Typography>
                                                        <Typography
                                                            variant="subtitle2"
                                                            color="success.main"
                                                            sx={{
                                                                fontWeight: 600,
                                                                fontSize:
                                                                    "0.95rem",
                                                            }}
                                                        >
                                                            {formatCurrencyBDT(
                                                                Number(
                                                                    product.effective_price,
                                                                ) || 0,
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography
                                                        variant="subtitle2"
                                                        color="primary"
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: "0.95rem",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {formatCurrencyBDT(
                                                            Number(
                                                                product.selling_price,
                                                            ) || 0,
                                                        )}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        sx={{
                                            my: 0,
                                            "& .MuiListItemText-primary": {
                                                mb: 0.5,
                                            },
                                        }}
                                    />
                                </ListItem>
                            );
                        })}
                        {filteredProducts.length === 0 && searchTerm.trim() !== '' && (
                            <Box
                                sx={{
                                    py: 3,
                                    textAlign: 'center',
                                    border: '2px dashed',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    bgcolor: 'background.paper',
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    No products found matching "{searchTerm}"
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={onQuickAddProduct}
                                    sx={{ mb: 1 }}
                                >
                                    Quick Add Product
                                </Button>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Add this product quickly for this sale
                                </Typography>
                            </Box>
                        )}
                    </List>
                </CardContent>
            </Card>

            <BarcodeScannerDialog
                open={scannerOpen}
                onClose={() => setScannerOpen(false)}
                onScan={handleBarcodeScan}
                title="Scan Product Barcode"
            />
        </>
    );
};
